import { Extension } from '@tiptap/core'
import { Mark } from '@tiptap/pm/model'
import { aiChangesPluginKey } from './ai-changes/ai-changes-extension'

// Types from TipTap Pro AI specification
export type Tone = 'default' | 'academic' | 'business' | 'casual' | 'childfriendly' | 'confident' | 'conversational' | 'creative' | 'emotional' | 'excited' | 'formal' | 'friendly' | 'funny' | 'humorous' | 'informative' | 'inspirational' | 'memeify' | 'narrative' | 'objective' | 'persuasive' | 'poetic' | string

export type Language = 'en' | 'de' | 'nl' | 'fr' | 'es' | 'it' | 'pt' | 'ru' | 'ja' | 'ko' | 'zh' | 'ar' | string

export interface TextOptions {
  format?: 'json' | 'markdown' | 'plain-text'
  stream?: boolean
  insertAt?: undefined | number | { from: number; to: number }
  language?: string
  tone?: string
  textLength?: number
  textLengthUnit?: string
  collapseToEnd?: boolean
  context?: Array<{ type: 'text', text: string } | { type: 'url', url: string }>
  text?: string
  insert?: boolean
  append?: boolean
  regenerate?: boolean
  // External stream handling
  responseStream?: ReadableStream<Uint8Array>
  abortSignal?: AbortSignal
}

// Define the AI commands available
export interface AICommands {
  aiRestructure: (options?: TextOptions) => any
  aiBloggify: (options?: TextOptions) => any
  aiKeypoints: (options?: TextOptions) => any
  aiRephrase: (options?: TextOptions) => any
  aiShorten: (options?: TextOptions) => any
  aiExtend: (options?: TextOptions) => any
  aiSummarize: (options?: TextOptions) => any
  aiSimplify: (options?: TextOptions) => any
  aiFixSpellingAndGrammar: (options?: TextOptions) => any
  aiComplete: (options?: TextOptions) => any
  aiAdjustTone: (tone: Tone, options?: TextOptions) => any
  aiEmojify: (options?: TextOptions) => any
  aiDeEmojify: (options?: TextOptions) => any
  aiTranslate: (language: Language, options?: TextOptions) => any
  aiTldr: (options?: TextOptions) => any
  aiTextPrompt: (options?: TextOptions) => any
  aiAccept: (options?: { insertAt?: number | { from: number; to: number } }) => any
  aiReject: () => any
  aiRegenerate: (options?: TextOptions) => any
}

export interface AIExtensionOptions {
  apiEndpoint?: string
  // Function to create a stream for AI requests
  createStream?: (prompt: { system: string; user: string }, signal?: AbortSignal) => Promise<ReadableStream<Uint8Array>>
  // Event handlers
  onLoading?: () => void
  onError?: (error: Error) => void
}

export interface AIStorage {
  status?: 'idle' | 'loading' | 'success' | 'error'
  message?: string
  error?: Error
  lastCommand?: any
  lastResponse?: string | undefined
  lastInsertionRange?: number | { from: number; to: number } | null
  currentAbortController?: AbortController | null
}

// Command prompts - these define what each AI command does
const COMMAND_PROMPTS = {
  restructure: {
    system: 'You are an expert content editor. Restructure the provided content to improve flow, organization, and readability while preserving all key information.',
    prompt: 'Restructure this content to improve its organization and flow:'
  },
  bloggify: {
    system: 'You are a professional blog writer. Transform the provided content into an engaging, well-structured blog post with compelling headlines and smooth transitions.',
    prompt: 'Transform this content into an engaging blog post:'
  },
  keypoints: {
    system: 'You are an expert at summarizing content. Extract the key points from the provided content and present them in a clear, organized format.',
    prompt: 'Extract the key points from this content:'
  },
  rephrase: {
    system: 'You are an expert writer. Rephrase the provided content to improve clarity and readability while maintaining the original meaning.',
    prompt: 'Rephrase this content to improve clarity:'
  },
  shorten: {
    system: 'You are an expert editor. Shorten the provided content while preserving all essential information and maintaining readability.',
    prompt: 'Shorten this content while keeping all essential information:'
  },
  extend: {
    system: 'You are an expert writer. Expand on the provided content by adding relevant details, examples, and context while maintaining the original tone and style.',
    prompt: 'Expand on this content with additional relevant details:'
  },
  summarize: {
    system: 'You are an expert at creating summaries. Create a comprehensive summary of the provided content that captures all key points concisely.',
    prompt: 'Summarize this content:'
  },
  simplify: {
    system: 'You are an expert at making complex content accessible. Simplify the provided content to make it easier to understand while preserving all important information.',
    prompt: 'Simplify this content to make it easier to understand:'
  },
  fixSpellingAndGrammar: {
    system: 'You are an expert proofreader. Fix all spelling, grammar, and punctuation errors in the provided content while maintaining the original meaning and style.',
    prompt: 'Fix all spelling and grammar errors in this content:'
  },
  complete: {
    system: 'You are an expert writer. Continue the provided content in a natural, coherent way that matches the existing tone and style.',
    prompt: 'Continue writing this content:'
  },
  emojify: {
    system: 'You are an expert at enhancing content with emojis. Add appropriate emojis to the provided content to make it more engaging and expressive.',
    prompt: 'Add appropriate emojis to this content:'
  },
  deEmojify: {
    system: 'You are an expert editor. Remove all emojis from the provided content while maintaining readability and flow.',
    prompt: 'Remove all emojis from this content:'
  },
  translate: {
    system: 'You are an expert translator. Translate the provided content accurately while maintaining the original tone, style, and meaning.',
    prompt: (language: string) => `Translate this content to ${language}:`
  },
  tldr: {
    system: 'You are an expert at creating concise summaries. Create a very brief "too long; didn\'t read" summary of the provided content.',
    prompt: 'Create a tl;dr summary of this content:'
  },
  adjustTone: {
    system: 'You are an expert writer. Adjust the tone of the provided content to match the specified tone while maintaining the original meaning.',
    prompt: (tone: string) => `Adjust the tone of this content to be ${tone}:`
  },
  textPrompt: {
    system: 'You are an expert assistant. Follow the custom instructions provided by the user.',
    prompt: (customPrompt: string) => customPrompt
  }
}

// Helper functions
const buildPrompt = (commandType: string, selectedText: string, options: TextOptions) => {
  const commandConfig = COMMAND_PROMPTS[commandType as keyof typeof COMMAND_PROMPTS]
  
  if (!commandConfig) {
    throw new Error(`Unknown command type: ${commandType}`)
  }

  let promptText: string
  if (typeof commandConfig.prompt === 'function') {
    // Handle different function signatures
    if (commandType === 'textPrompt') {
      promptText = commandConfig.prompt(options.text || 'Please provide instructions.')
    } else {
      promptText = commandConfig.prompt(options.tone || options.language || '')
    }
  } else {
    promptText = commandConfig.prompt
  }

  // Build the user prompt with context if provided
  let userPrompt = promptText
  
  // Add context if provided
  if (options.context && options.context.length > 0) {
    const contextText = options.context
      .map(ctx => ctx.type === 'text' ? ctx.text : `[URL: ${ctx.url}]`)
      .join('\n')
    userPrompt += `\n\nContext:\n${contextText}`
  }
  
  // Add selected text if available
  if (selectedText) {
    userPrompt += `\n\n${selectedText}`
  }
  
  // Add custom text if provided (for textPrompt)
  if (options.text && commandType !== 'textPrompt') {
    userPrompt += `\n\nAdditional instructions: ${options.text}`
  }

  // Add length constraints if specified
  if (options.textLength && options.textLengthUnit) {
    userPrompt += `\n\nPlease limit your response to ${options.textLength} ${options.textLengthUnit}.`
  }

  return {
    system: commandConfig.system,
    user: userPrompt,
    format: options.format || 'plain-text',
    language: options.language,
    tone: options.tone
  }
}

// Simple AI request handler
const processAICommand = async (
  extension: any,
  editor: any,
  commandType: string,
  selectedText: string,
  selection: { from: number, to: number, empty: boolean },
  options: TextOptions
) => {
  // Store the last command for regenerate functionality
  extension.storage.lastCommand = { commandType, selectedText, selection, options }
  
  // Determine insertion position
  let insertionRange: number | { from: number, to: number } | null = null
  
  if (options.insertAt !== undefined) {
    insertionRange = options.insertAt
  } else if (options.append && !selection.empty) {
    insertionRange = selection.to
  } else if (selection.empty) {
    insertionRange = selection.from
  } else {
    insertionRange = { from: selection.from, to: selection.to }
  }

  const { onLoading, onError } = extension.options
  extension.storage.currentAbortController = new AbortController()

  // Get all marks from the selected text (moved outside to be accessible in streaming)
  const existingMarks: Mark[] = []
  if (insertionRange && typeof insertionRange === 'object' && selectedText) {
    editor.state.doc.nodesBetween(insertionRange.from, insertionRange.to, (node: any) => {
      if (node.marks) {
        existingMarks.push(...node.marks)
      }
    })
  }

  try {
    const prompt = buildPrompt(commandType, selectedText, options)
    
    // Set loading state
    extension.storage.status = 'loading'
    extension.storage.message = selectedText // Show original text while loading
    extension.storage.error = undefined
    onLoading?.()

    let stream: ReadableStream<Uint8Array>
    
    if (options.responseStream) {
      stream = options.responseStream
    } else if (extension.options.createStream) {
      stream = await extension.options.createStream(prompt, extension.storage.currentAbortController.signal)
    } else {
      // Fallback to internal API call
      const token = localStorage.getItem('auth:token')
      const workspaceId = localStorage.getItem('auth:activeWorkspaceId')
      const response = await fetch(extension.options.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Workspace-Id': workspaceId || ''
        },
        body: JSON.stringify({
          messages: [
            { role: 'user', content: prompt.user }
          ],
          system: prompt.system
        }),
        signal: extension.storage.currentAbortController.signal
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      if (!response.body) {
        throw new Error('No response body')
      }
      
      stream = response.body
    }

    // 🚀 LIVE DOCUMENT STREAMING: First mark the old text as removed
    let currentPosition = typeof insertionRange === 'number' 
      ? insertionRange 
      : (insertionRange?.to || 0) // Use .to instead of .from to append after selected text
    
    if (insertionRange && typeof insertionRange === 'object' && selectedText) {
      // Keep the original text with all its marks plus aiChangeOld
      editor.chain()
        .setTextSelection({ from: insertionRange.from, to: insertionRange.to })
        .setMark('aiChangeOld')
        .run()
    } else if (typeof insertionRange === 'number') {
      // If we're inserting at a specific position, ensure we're in a valid block node
      const pos = insertionRange
      const $pos = editor.state.doc.resolve(pos)
      if (!$pos.parent.isTextblock) {
        // Create a new paragraph if we're not in a text block
        editor.chain()
          .insertContentAt(pos, { type: 'paragraph', content: [] })
          .run()
      }
    }
    
    console.log('🎯 Starting live streaming at position:', currentPosition)

    // Process the stream and insert chunks directly into document
    const reader = stream.getReader()
    const decoder = new TextDecoder()
    let fullResponse = ''
    
    while (true) {
      const { done, value } = await reader.read()
      
      if (done) break

      const chunk = decoder.decode(value, { stream: true })
      
      // Check if this is a simple text stream (for mock testing) or Vercel AI SDK format
      if (!chunk.includes('0:') && !chunk.includes('f:') && !chunk.includes('e:') && !chunk.includes('d:')) {
        // Simple text stream - append chunk
        console.log('📝 Simple text chunk:', chunk)
        
        fullResponse += chunk
        
        // Insert chunk with marks
        const insertNode = { 
          type: 'text', 
          text: chunk, 
          marks: [
            { type: 'aiChange' },
            ...(existingMarks?.filter(mark => mark.type.name !== 'aiChangeOld') || [])
          ]
        }
        
        // Insert at current position and update position
        editor.chain()
          .insertContentAt(currentPosition, [insertNode])
          .run()
        
        // Recalculate currentPosition after insertion
        const docAfter = editor.state.doc
        const $pos = docAfter.resolve(currentPosition)
        if ($pos.nodeAfter && $pos.nodeAfter.isText) {
          currentPosition = $pos.pos + $pos.nodeAfter.nodeSize
        } else {
          currentPosition = $pos.pos
        }
        
        // Update storage for preview
        editor.commands.command(() => {
          extension.storage.message = fullResponse
          return true
        })
      } else {
        // Parse Vercel AI SDK data stream format
        const lines = chunk.split('\n')
        
        for (const line of lines) {
          if (line.startsWith('0:')) {
            // Text chunk
            const textData = line.slice(2)
            let text: string
            
            try {
              text = JSON.parse(textData)
            } catch {
              text = textData
            }
            
            console.log('📝 Vercel AI text chunk:', text)
            fullResponse += text
            
            // Insert chunk with marks
            const insertNode = { 
              type: 'text', 
              text: text, 
              marks: [
                { type: 'aiChange' },
                ...(existingMarks?.filter(mark => mark.type.name !== 'aiChangeOld') || [])
              ]
            }
            editor.chain()
              .insertContentAt(currentPosition, [insertNode])
              .run()
            
            // Recalculate currentPosition after insertion
            const docAfter = editor.state.doc
            const $pos = docAfter.resolve(currentPosition)
            if ($pos.nodeAfter && $pos.nodeAfter.isText) {
              currentPosition = $pos.pos + $pos.nodeAfter.nodeSize
            } else {
              currentPosition = $pos.pos
            }
            
            // Update storage for preview
            editor.commands.command(() => {
              extension.storage.message = fullResponse
              return true
            })
          } else if (line.startsWith('d:')) {
            // Done - finish streaming
            break
          } else if (line.startsWith('f:')) {
            // Metadata - log it
            console.log('📊 Stream metadata:', line.slice(2))
          } else if (line.startsWith('e:')) {
            // End metadata - log it
            console.log('🏁 Stream end metadata:', line.slice(2))
          }
        }
      }
    }

    // Store response for utility commands
    extension.storage.lastResponse = fullResponse
    extension.storage.lastInsertionRange = insertionRange
    extension.storage.status = 'success'
    
    // Force AI Changes plugin to recompute the diff after streaming
    if (editor && editor.view && editor.state) {
      editor.view.dispatch(editor.state.tr.setMeta(
        aiChangesPluginKey,
        { type: 'updateChanges' }
      ))
    }
    console.log('✅ Live streaming complete! Final text length:', fullResponse.length)

  } catch (error: unknown) {
    if (error instanceof Error && error.name !== 'AbortError') {
      console.error('AI command error:', error)
      extension.storage.status = 'error'
      extension.storage.error = error as Error
      onError?.(error as Error)

      // Restore original text on error if we were replacing
      if (insertionRange && typeof insertionRange === 'object') {
        editor.chain()
          .focus()
          .deleteRange(insertionRange)
          .insertContentAt(insertionRange.from, [{ type: 'text', text: selectedText }])
          .run()
      }
    }
  } finally {
    extension.storage.currentAbortController = null
  }
}

declare module '@tiptap/core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Commands<ReturnType> {
    aiExtension: AICommands
  }
}

export const AIExtension = Extension.create<AIExtensionOptions, AIStorage>({
  name: 'aiExtension',

  addOptions() {
    return {
      apiEndpoint: `${import.meta.env.VITE_SERVER_URL}/api/ai/editor`,
      createStream: undefined,
      onLoading: undefined,
      onError: undefined,
    }
  },

  addStorage() {
    return {
      status: 'idle',
      message: undefined,
      error: undefined,
      lastCommand: null,
      lastResponse: undefined,
      lastInsertionRange: null,
      currentAbortController: null,
    }
  },

  addCommands() {
    // Helper to create AI commands
    const createAICommand = (commandType: string, optionsOverride: TextOptions = {}) => {
      return ({ editor, state }: any) => {
        if (this.storage.status === 'loading') {
          return false
        }

        const { from, to, empty } = state.selection
        
        // Get selected text or use current position for completion
        const selectedText = empty ? '' : state.doc.textBetween(from, to, ' ')
        
        // Only require selected text for commands that need it
        if (empty && commandType !== 'complete' && commandType !== 'textPrompt') {
          return false 
        }

        // Default options with TipTap Pro compatibility
        const defaultOptions: TextOptions = {
          stream: true,
          format: 'plain-text',
          insert: true,
          append: false,
          collapseToEnd: true,
          ...optionsOverride
        }

        // Start the AI processing
        processAICommand(
          this,
          editor,
          commandType,
          selectedText,
          { from, to, empty },
          defaultOptions
        )

        return true
      }
    }

    return {
      aiRestructure: (options = {}) => createAICommand('restructure', options),
      aiBloggify: (options = {}) => createAICommand('bloggify', options),
      aiKeypoints: (options = {}) => createAICommand('keypoints', options),
      aiRephrase: (options = {}) => createAICommand('rephrase', options),
      aiShorten: (options = {}) => createAICommand('shorten', options),
      aiExtend: (options = {}) => createAICommand('extend', options),
      aiSummarize: (options = {}) => createAICommand('summarize', options),
      aiSimplify: (options = {}) => createAICommand('simplify', options),
      aiFixSpellingAndGrammar: (options = {}) => createAICommand('fixSpellingAndGrammar', options),
      aiComplete: (options = {}) => createAICommand('complete', options),
      aiAdjustTone: (tone: Tone, options = {}) => createAICommand('adjustTone', { ...options, tone }),
      aiEmojify: (options = {}) => createAICommand('emojify', options),
      aiDeEmojify: (options = {}) => createAICommand('deEmojify', options),
      aiTranslate: (language: Language, options = {}) => createAICommand('translate', { ...options, language }),
      aiTldr: (options = {}) => createAICommand('tldr', options),
      aiTextPrompt: (options = {}) => createAICommand('textPrompt', options),
      
      // Utility commands
      aiAccept: (options = {}) => ({ editor }: any) => {
        if (!this.storage.lastResponse) {
          console.warn('No AI response to accept')
          return false
        }
        
        const insertionRange = options.insertAt || this.storage.lastInsertionRange
        if (insertionRange) {
          editor.chain()
            .deleteRange(typeof insertionRange === 'number' 
              ? { from: insertionRange, to: insertionRange }
              : insertionRange
            )
            .insertContentAt(
              typeof insertionRange === 'number' ? insertionRange : insertionRange.from,
              this.storage.lastResponse
            )
            .run()
        }
        
        return true
      },
      
      aiReject: () => () => {
        this.storage.lastResponse = undefined
        this.storage.lastCommand = null
        this.storage.lastInsertionRange = null
        this.storage.status = 'idle'
        this.storage.message = undefined
        this.storage.error = undefined
        console.log('AI response rejected and storage cleared')
        return true
      },
      
      aiRegenerate: (options = {}) => ({ editor, _state }: any) => {
        if (!this.storage.lastCommand) {
          console.warn('No previous AI command to regenerate')
          return false
        }
        
        const { commandType, selectedText, selection } = this.storage.lastCommand
        const mergedOptions = { ...this.storage.lastCommand.options, ...options, regenerate: true }
        
        processAICommand(this, editor, commandType, selectedText, selection, mergedOptions)
        return true
      },
    }
  },

  onDestroy() {
    // Clean up any ongoing requests
    if (this.storage.currentAbortController) {
      this.storage.currentAbortController.abort()
    }
  },
})

export default AIExtension 